import React, { useState, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Label, Select, SelectItem, DateInput, Input } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { ChevronDownIcon } from '../../../components/ui/Icons';
import { renderField } from '../../../components/generic/fieldRenderers';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * DeviationImplementationStatusIntegration - Component for managing implementation status of deviation actions
 * Displays implementation tracking fields for each CAPA action (1:1 relationship)
 */
const DeviationImplementationStatusIntegration = ({ 
  deviationId,
  formData,
  setFormData,
  config,
  fields,
  isEditMode = false,
  selectedRecord = null // For view mode
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine if we're in view mode
  const isViewMode = selectedRecord && !isEditMode;
  const recordId = selectedRecord?.id || deviationId || formData?.id;
  const recordData = isViewMode ? selectedRecord : formData;

  // Fetch lookup data for action types (to display readonly action type and get record_order)
  const { data: actionTypes = [], isLoading: actionTypesLoading } = useFlexibleList(
    config?.api?.lookupEndpoints?.deviationActionTypes ? config.api.lookupEndpoints.deviationActionTypes : null
  );

  // Fetch existing actions if recordId exists (for both edit and view mode)
  const { data: existingActions = [] } = useFlexibleList(
    recordId ? `/deviation-action/by-deviation/${recordId}` : null
  );

  // Helper function to get action type record_order
  const getActionTypeOrder = useCallback((action) => {
    // First try to get from populated actionType relation
    if (action.actionType?.record_order !== null && action.actionType?.record_order !== undefined) {
      return action.actionType.record_order;
    }
    // Otherwise, look it up from actionTypes data
    if (action.actionTypeId && actionTypes.length > 0) {
      const actionType = actionTypes.find(type => type.id === action.actionTypeId);
      if (actionType?.record_order !== null && actionType?.record_order !== undefined) {
        return actionType.record_order;
      }
    }
    // Fallback to ID if no record_order
    return action.id || 0;
  }, [actionTypes]);

  // Get actions: 
  // In edit mode: always use formData.deviationActions (even if empty) to reflect immediate deletions
  // In view mode: prioritize existingActions (from API) > selectedRecord.actions
  let actions;
  if (isEditMode) {
    // In edit mode, always use formData to reflect immediate changes (including deletions)
    // Even if empty, we want to show empty to reflect that actions were deleted
    actions = Array.isArray(recordData?.deviationActions) ? recordData.deviationActions : [];
  } else if (existingActions && existingActions.length > 0) {
    // In view mode, use existingActions from API
    actions = existingActions;
  } else {
    // Fallback to selectedRecord actions or empty array
    actions = recordData?.deviationActions || [];
  }

  // Sort actions by actionType.record_order to ensure consistent order between CAPA and Implementation sections
  actions = [...actions].sort((a, b) => {
    const orderA = getActionTypeOrder(a);
    const orderB = getActionTypeOrder(b);
    
    // Sort by record_order, then by ID as fallback
    if (orderA !== orderB) {
      return (orderA || 999999) - (orderB || 999999);
    }
    return (a.id || 0) - (b.id || 0);
  });

  const updateAction = (index, field, value) => {
    if (isViewMode || !setFormData) return;
    
    console.log(`🔄 updateAction called: index=${index}, field=${field}, value=`, value);
    
    setFormData(prev => {
      const currentActions = prev.deviationActions || [];
      const newActions = [...currentActions];
      if (newActions[index]) {
        const oldValue = newActions[index][field];
        newActions[index] = {
          ...newActions[index],
          [field]: value
        };
        console.log(`✅ Updated action[${index}].${field}:`, { oldValue, newValue: value, fullAction: newActions[index] });
      } else {
        console.warn(`⚠️ Action at index ${index} not found`);
      }
      return {
        ...prev,
        deviationActions: newActions
      };
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const getActionTypeName = (actionTypeId) => {
    if (!actionTypeId) return '—';
    const actionType = actionTypes.find(type => type.id === actionTypeId);
    return actionType?.name || '—';
  };

  // Get implementation and implementationCompletedAt fields
  const implementationField = fields?.find(f => f.name === 'implementation');
  const implementationCompletedAtField = fields?.find(f => f.name === 'implementationCompletedAt');

  // In view mode, render like Risk Assessment section - simple grid layout
  if (isViewMode) {
    return (
      <div className="space-y-4">
        {/* Implementation fields in grid layout like Risk Assessment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {implementationField && renderField(implementationField, {
            value: recordData?.implementation || false,
            formData: recordData,
            setFormData,
            formErrors: {},
            setFormErrors: () => {},
            validateField: async () => null,
            labels: {},
            config,
            setFilesToDelete: () => {},
            theme
          })}
          {implementationCompletedAtField && renderField(implementationCompletedAtField, {
            value: recordData?.implementationCompletedAt || '',
            formData: recordData,
            setFormData,
            formErrors: {},
            setFormErrors: () => {},
            validateField: async () => null,
            labels: {},
            config,
            setFilesToDelete: () => {},
            theme
          })}
        </div>

        {/* Implementation Actions Status - Show below in view mode if there are actions */}
        {actions.length > 0 && (
          <div className={cn("border rounded-lg mt-4", theme.border.light, theme.colors.background)}>
            {/* Accordion Header */}
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                theme.text.primary
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">📊</span>
                <span className="font-semibold">Implementation Actions Status</span>
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium",
                  theme.colors.primary,
                  theme.text.inverse
                )}>
                  {actions.length}
                </span>
              </div>
              {isExpanded ? (
                <ChevronUpIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
            </button>

            {/* Accordion Content */}
            {isExpanded && (
              <div className={cn("p-4 border-t", theme.border.light)}>
                <div className="space-y-4">
                  {actions.map((action, index) => (
                    <div
                      key={action.id || index}
                      className={cn(
                        "p-4 rounded-lg border space-y-4",
                        theme.border.subtle,
                        theme.colors.backgroundAlt
                      )}
                    >
                      {/* Action Header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={cn("text-sm font-medium", theme.text.secondary)}>
                          Implementation Status #{index + 1}
                        </span>
                        <span className={cn("text-xs", theme.text.muted)}>
                          (for Action: {getActionTypeName(action.actionTypeId)})
                        </span>
                      </div>

                      {/* Implementation Status Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {/* Action Type - Readonly */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-actionType-${index}`}>
                            Action Type
                          </Label>
                          <div className={cn(
                            "text-sm font-bold p-3 rounded-lg border-2",
                            theme.border.light,
                            theme.colors.primary,
                            theme.text.inverse,
                            "flex items-center justify-center min-h-[42px]"
                          )}>
                            {getActionTypeName(action.actionTypeId)}
                          </div>
                        </div>

                        {/* Implementation Status */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-implementationStatus-${index}`}>
                            Implementation Status
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.implementationStatus || '—'}
                          </div>
                        </div>

                        {/* New Extension */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-newExtension-${index}`}>
                            New Extension
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newExtension ? new Date(action.newExtension).toLocaleDateString() : '—'}
                          </div>
                        </div>

                        {/* QA Responsible */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-qaResponsible-${index}`}>
                            QA Responsible
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.qaResponsible || '—'}
                          </div>
                        </div>

                        {/* QA Date */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-qaDate-${index}`}>
                            QA Date
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.qaDate ? new Date(action.qaDate).toLocaleDateString() : '—'}
                          </div>
                        </div>

                        {/* New Due Date */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-newDueDate-${index}`}>
                            New Due Date
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newDueDate ? new Date(action.newDueDate).toLocaleDateString() : '—'}
                          </div>
                        </div>

                        {/* New Implementation Status */}
                        <div className="md:col-span-4">
                          <Label htmlFor={`impl-newImplementationStatus-${index}`}>
                            New Implementation Status
                          </Label>
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newImplementationStatus || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Edit mode - show full component with editable fields
  return (
    <div className="space-y-4">
      {/* Implementation checkbox and date - render regular fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {implementationField && renderField(implementationField, {
          value: recordData?.implementation || false,
          formData: recordData,
          setFormData,
          formErrors: {},
          setFormErrors: () => {},
          validateField: async () => null,
          labels: {},
          config,
          setFilesToDelete: () => {},
          theme
        })}
        {implementationCompletedAtField && renderField(implementationCompletedAtField, {
          value: recordData?.implementationCompletedAt || '',
          formData: recordData,
          setFormData,
          formErrors: {},
          setFormErrors: () => {},
          validateField: async () => null,
          labels: {},
          config,
          setFilesToDelete: () => {},
          theme
        })}
      </div>

      {/* Implementation Actions Status Accordion */}
      <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
        {/* Accordion Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            theme.text.primary
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">📊</span>
            <span className="font-semibold">Implementation Actions Status</span>
            {actions.length > 0 && (
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-medium",
                theme.colors.primary,
                theme.text.inverse
              )}>
                {actions.length}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>

        {/* Accordion Content */}
        {isExpanded && (
          <div className={cn("p-4 border-t", theme.border.light)}>
            {actions.length === 0 ? (
              <div className={cn("text-center py-8", theme.text.secondary)}>
                <p>No CAPA actions found. Add actions in the "Corrective and Preventive Actions (CAPA)" section first.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {actions.map((action, index) => (
                  <div
                    key={action.id || index}
                    className={cn(
                      "p-4 rounded-lg border space-y-4",
                      theme.border.subtle,
                      theme.colors.backgroundAlt
                    )}
                  >
                    {/* Action Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Implementation Status #{index + 1}
                      </span>
                      <span className={cn("text-xs", theme.text.muted)}>
                        (for Action: {getActionTypeName(action.actionTypeId)})
                      </span>
                    </div>

                    {/* Implementation Status Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      {/* Action Type - Readonly */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-actionType-${index}`}>
                          Action Type
                        </Label>
                        <div className={cn(
                          "text-sm font-bold p-3 rounded-lg border-2",
                          theme.border.light,
                          theme.colors.primary,
                          theme.text.inverse,
                          "flex items-center justify-center min-h-[42px]"
                        )}>
                          {getActionTypeName(action.actionTypeId)}
                        </div>
                      </div>

                      {/* Implementation Status */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-implementationStatus-${index}`}>
                          Implementation Status
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.implementationStatus || '—'}
                          </div>
                        ) : (
                          <Select
                            id={`impl-implementationStatus-${index}`}
                            value={action.implementationStatus || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateAction(index, 'implementationStatus', value === '' ? null : value);
                            }}
                          >
                            <SelectItem value="">Select status</SelectItem>
                            <SelectItem value="Closeout">Closeout</SelectItem>
                            <SelectItem value="Extension">Extension</SelectItem>
                          </Select>
                        )}
                      </div>

                      {/* New Extension */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-newExtension-${index}`}>
                          New Extension
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newExtension ? new Date(action.newExtension).toLocaleDateString() : '—'}
                          </div>
                        ) : (
                          <DateInput
                            id={`impl-newExtension-${index}`}
                            value={formatDateForInput(action.newExtension)}
                            onChange={(e) => {
                              const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                              updateAction(index, 'newExtension', dateValue);
                            }}
                          />
                        )}
                      </div>

                      {/* QA Responsible */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-qaResponsible-${index}`}>
                          QA Responsible
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.qaResponsible || '—'}
                          </div>
                        ) : (
                          <Input
                            id={`impl-qaResponsible-${index}`}
                            type="text"
                            value={action.qaResponsible || ''}
                            onChange={(e) => updateAction(index, 'qaResponsible', e.target.value)}
                            placeholder="Enter QA responsible"
                          />
                        )}
                      </div>

                      {/* QA Date */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-qaDate-${index}`}>
                          QA Date
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.qaDate ? new Date(action.qaDate).toLocaleDateString() : '—'}
                          </div>
                        ) : (
                          <DateInput
                            id={`impl-qaDate-${index}`}
                            value={formatDateForInput(action.qaDate)}
                            onChange={(e) => {
                              const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                              updateAction(index, 'qaDate', dateValue);
                            }}
                          />
                        )}
                      </div>

                      {/* New Due Date */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-newDueDate-${index}`}>
                          New Due Date
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newDueDate ? new Date(action.newDueDate).toLocaleDateString() : '—'}
                          </div>
                        ) : (
                          <DateInput
                            id={`impl-newDueDate-${index}`}
                            value={formatDateForInput(action.newDueDate)}
                            onChange={(e) => {
                              const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                              updateAction(index, 'newDueDate', dateValue);
                            }}
                          />
                        )}
                      </div>

                      {/* New Implementation Status */}
                      <div className="md:col-span-4">
                        <Label htmlFor={`impl-newImplementationStatus-${index}`}>
                          New Implementation Status
                        </Label>
                        {isViewMode ? (
                          <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                            {action.newImplementationStatus || '—'}
                          </div>
                        ) : (
                          <Select
                            id={`impl-newImplementationStatus-${index}`}
                            value={action.newImplementationStatus || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateAction(index, 'newImplementationStatus', value === '' ? null : value);
                            }}
                          >
                            <SelectItem value="">Select status</SelectItem>
                            <SelectItem value="Closeout">Closeout</SelectItem>
                            <SelectItem value="Extension">Extension</SelectItem>
                          </Select>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviationImplementationStatusIntegration;

